import SideMenu from "../../../ui/SideMenu";
import styles from "../../../styles/Components/pricing-plan/individual-services/ServiceActivationBox.module.css";
import { calculateOffPrice } from "../../../utility/helper";
import { Button, Card, Modal, Slider, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { servicesCost as costs } from "../../../utility/service-activation";
import { useMemo, useState } from "react";
import useUserTier from "../../../hooks/useUserTier";
import PaymentOptionsModal from "../activate-services/PaymentOptionsModal";
import { BsPatchQuestionFill } from "react-icons/bs";
import { activateServiceWithCredit } from "../../../redux/pricing-plan/action";
import { closeSideMenu } from "../../../redux/ui/uiSlice";

const { Text, Title } = Typography;

const DurationCard = ({ months, discount, selected, onClick, price }) => (
  <div onClick={onClick} style={{ cursor: "pointer" }}>
    <Card
      className={`${styles.durationCard} ${
        selected ? styles.selectedDuration : ""
      }`}
      bordered={false}
    >
      <div className={styles.durationContent}>
        <Title level={5}>
          {months === "12" ? "1 Year" : `${months} Months`}
        </Title>
        {discount > 0 && (
          <div className={styles.discountBadge}>{discount}%</div>
        )}
        <Text className={styles.priceText}>${price}</Text>
      </div>
    </Card>
  </div>
);

function ServiceActivationMenu({ selectedServices, setSelectedServices }) {
  const {
    main: { userData },
    profile: { profileData, loading },
    pricingPlan: { pricingPlan, activateServiceWithCreditStatus },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const offPercentage =
    pricingPlan["yearly_discount"] || costs["yearly_discount"];

  const { serviceType: aiKey } = useUserTier("myais");
  const { serviceType: alertKey } = useUserTier("alert");
  const { serviceType: botKey } = useUserTier("mybot");
  const { serviceType: insightKey } = useUserTier("insai");
  const { serviceType: tradeSimulatorKey } = useUserTier("trsim");

  const servicesCost = useMemo(() => {
    return {
      myais: pricingPlan[aiKey],
      alert: pricingPlan[alertKey],
      mybot: pricingPlan[botKey],
      insai: pricingPlan[insightKey],
      trsim: pricingPlan[tradeSimulatorKey],
    };
  }, [aiKey, alertKey, botKey, insightKey, pricingPlan, tradeSimulatorKey]);

  const totalPrice = selectedServices.reduce(
    (total, service) => total + servicesCost[service],
    0,
  );

  const [selectedDuration, setSelectedDuration] = useState("1");
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);

  const finalPrice =
    selectedDuration === "1"
      ? totalPrice
      : calculateOffPrice(offPercentage, totalPrice, selectedDuration, "price");

  const durations = [
    { months: "1", discount: 0 },
    { months: "3", discount: calculateOffPrice(offPercentage, 0, 3, "off") },
    { months: "6", discount: calculateOffPrice(offPercentage, 0, 6, "off") },
    { months: "12", discount: calculateOffPrice(offPercentage, 0, 12, "off") },
  ];

  const handleDurationSelect = (duration) => {
    if (selectedServices.length === 0) return;
    if (duration === "custom") {
      setShowCustomDuration(true);
      setSelectedDuration("1");
    } else {
      setShowCustomDuration(false);
      setSelectedDuration(duration);
    }
  };

  const servicesString = selectedServices.join("_");
  const activationMode = `activate_${servicesString}`;
  const activateService = () => {
    if (activateServiceWithCreditStatus === "loading") return;
    const url = window.location.href;

    if (finalPrice <= profileData?.credit / 100) {
      return Modal.confirm({
        title: "Confirm Service Activation",
        content: "Are you sure you want to activate these services?",
        centered: true,
        okText: "Activate",
        icon: <BsPatchQuestionFill size={18} color="#1677ff" />,
        okButtonProps: {
          style: {
            backgroundColor: "#0c3fce",
            borderColor: "#0c3fce",
          },
          loading: activateServiceWithCreditStatus === "loading",
        },
        onOk: () => {
          dispatch(
            activateServiceWithCredit(
              userData?.["custom:custom_username"],
              userData?.email,
              activationMode,
              `${finalPrice}`,
              url,
              url,
              true,
            ),
          );
          setSelectedServices([]);
          dispatch(closeSideMenu());
        },
      });
    } else {
      setShowPaymentsModal(true);
      dispatch(closeSideMenu());
    }
  };

  const closePaymentModal = () => {
    setSelectedServices([]);
    setShowPaymentsModal(false);
    dispatch(closeSideMenu());
  };

  return (
    <SideMenu title="Activate Service" scope="active-service">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className={`${styles.section}`}>
          <Title level={3}>Select Duration</Title>
          <div className={styles.durationsGrid}>
            {durations.map(({ months, discount }) => (
              <DurationCard
                key={months}
                months={months}
                discount={discount}
                selected={selectedDuration === months}
                onClick={() => handleDurationSelect(months)}
                price={
                  months === "1"
                    ? totalPrice
                    : calculateOffPrice(
                        offPercentage,
                        totalPrice,
                        months,
                        "price",
                      )
                }
              />
            ))}
            <DurationCard
              months="custom"
              discount={0}
              selected={showCustomDuration}
              onClick={() => handleDurationSelect("custom")}
              price={totalPrice * (selectedDuration || 1)}
            />
          </div>

          {showCustomDuration && (
            <div className={styles.sliderContainer}>
              <Slider
                min={1}
                max={12}
                value={+selectedDuration || 1}
                onChange={(e) => setSelectedDuration(`${e}`)}
                tooltip={{
                  formatter: (value) => `${value} Month(s)`,
                  open: true,
                  color: "geekblue",
                  placement: "bottom",
                }}
              />
            </div>
          )}
        </div>

        <Card className={styles.summaryCard}>
          <div className={styles.summaryContent}>
            <div className={styles.summaryItem}>
              <Text>Discount</Text>
              <Text type={selectedDuration === "1" ? "secondary" : "success"}>
                {selectedDuration === "1"
                  ? "$0"
                  : `$${calculateOffPrice(
                      offPercentage,
                      totalPrice,
                      +selectedDuration,
                      "offeredPrice",
                    ).toFixed(2)} (${calculateOffPrice(
                      offPercentage,
                      totalPrice,
                      +selectedDuration,
                      "off",
                    )}%)`}
              </Text>
            </div>
            <div className={styles.summaryItem}>
              <Text>Original Price</Text>
              <Text type="secondary">
                ${totalPrice * (selectedDuration || 1)}
              </Text>
            </div>
            <div className={styles.summaryItem}>
              <Text strong>Final Price</Text>
              <Text
                strong
                type={selectedDuration === "1" ? "secondary" : "success"}
              >
                ${finalPrice}
              </Text>
            </div>
          </div>

          <Button
            type="primary"
            size="large"
            block
            disabled={!selectedServices.length}
            loading={activateServiceWithCreditStatus === "loading"}
            onClick={activateService}
            className={styles.activateButton}
          >
            Activate Services
          </Button>
        </Card>
        {showPaymentsModal && (
          <PaymentOptionsModal
            show={showPaymentsModal}
            setShow={closePaymentModal}
            activationMode={activationMode}
            amount={finalPrice}
          />
        )}
      </div>
    </SideMenu>
  );
}

export default ServiceActivationMenu;
